import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { LoadableLazy } from '@confluence/loadable';

import { buildAtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';

import { AtAGlanceSection } from './AtAGlanceSection.tsx';
import { LinksGroup } from './LinksGroup.tsx';
import { PeopleAndAgentsSection } from './PeopleAndAgentsSection.tsx';
import type { ConnectionSectionsData } from './useConnectionSectionsData.ts';
import { getSectionExpansionProps } from './useSectionExpansion.ts';
import type { SectionExpansionController } from './useSectionExpansion.ts';

const styles = cssMap({
	connectionsColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		boxSizing: 'border-box',
	},
});

// The rows of "Related content" carry the Smart Link hover card, which brings the flexible-card
// view and the AI summary blocks with it. This component ships in the byline's after-paint chunk
// on every view page, so that section is a lazy boundary and loads once the panel or the modal
// mounts it. Nothing stands in while its chunk loads: the section is absent while its graph
// query is in flight anyway.
const RelatedContentSection = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RelatedContentSection" */ './RelatedContentSection.tsx'
			)
		).RelatedContentSection,
});

export type ConnectionSectionsProps = {
	connections: ConnectionSectionsData;
	/**
	 * The cc-smarts response, fetched once by the surface that owns the connections. Not
	 * fetched here: the panel and the modal both render this component at the same time, so a
	 * call in here is a second paid LLM request for an answer already on screen.
	 */
	atAGlance: UseAtAGlanceResult;
	onPreviewOpen?: () => void;
	onAskRovo?: (prompt?: string) => void;
	/**
	 * Opens Rovo chat from the "Chat with Rovo" button at the end of "At a glance". Both surfaces
	 * pass it when Rovo is enabled, and both seed it with every connection url.
	 * Separate from `onAskRovo` because that one is bound to the `rowAction` trigger and seeds a
	 * single row's url.
	 */
	onAtAGlanceAskRovo?: () => void;
	/**
	 * Lifts each section's expanded state out to a caller that outlives the sections.
	 * Only the modal passes it, so its sections survive the switch to Rovo and back; the
	 * panel and popup omit it and let each section's shell own its own state.
	 */
	sectionExpansion?: SectionExpansionController;
};

/**
 * Both surfaces share the panel's data to avoid duplicate link and paid summary requests.
 * Only Related content rows show one-liners. Appears in and Links to stay in the summary
 * request as evidence for the overview, even though their rows do not show one-liners.
 */
export const ConnectionSections = ({
	connections,
	atAGlance,
	onPreviewOpen,
	onAskRovo,
	onAtAGlanceAskRovo,
	sectionExpansion,
}: ConnectionSectionsProps): React.JSX.Element => {
	const aiSummaries = useMemo(() => buildAtAGlanceSummaries(atAGlance.output), [atAGlance.output]);
	const peopleAndAgentsLoading =
		connections.people.loading ||
		connections.agentViewers.loading ||
		connections.agentContributors.loading;
	const peopleAndAgentsHasError = Boolean(
		connections.people.error ||
		connections.agentViewers.error ||
		connections.agentContributors.error ||
		connections.agentContributors.availability === 'unavailable',
	);

	return (
		<Box xcss={styles.connectionsColumn}>
			<AtAGlanceSection {...atAGlance} onAskRovo={onAtAGlanceAskRovo} />
			<RelatedContentSection
				{...connections.relatedContent}
				aiSummaries={aiSummaries}
				onPreviewOpen={onPreviewOpen}
				onAskRovo={onAskRovo}
				{...getSectionExpansionProps(sectionExpansion, 'relatedContent')}
			/>
			<LinksGroup
				syncedFrom={connections.syncedFrom}
				appearsIn={connections.appearsIn}
				linksTo={connections.linksTo}
				onPreviewOpen={onPreviewOpen}
				onAskRovo={onAskRovo}
				sectionExpansion={sectionExpansion}
			/>
			<PeopleAndAgentsSection
				people={connections.people.peopleAndAgents}
				loading={peopleAndAgentsLoading}
				hasError={peopleAndAgentsHasError}
				{...getSectionExpansionProps(sectionExpansion, 'people')}
			/>
		</Box>
	);
};
